/**
 * compose(합성/융합) 도메인 설치자 (S2c 후반) — 컨테이너 탭뷰 + MergeCommands/FusionCommands +
 * 셀 정보 팝업(등록/해제 버튼) 조립 + api{performModuleMerge/performModuleFusion} 기여.
 *
 * 합성=같은 이름·등급 3개→다음 등급 / 융합=같은 footprint 2개+비용(첫회 광고·이후 다이아).
 * featureFlags.moduleFusion=false면 합성 서브탭만 (뷰 옵션 게이트).
 */
import { ModuleComposeContainerTabView } from "../ui/ModuleComposeContainerTabView.js";
import { buildModuleFusionData, buildModuleMergeData } from "../ui/tabData.js";
import { openModuleInfoModal } from "../ui/moduleInfoModal.js";
import { MergeCommands } from "../app/commands/mergeCommands.js";
import { FusionCommands } from "../app/commands/fusionCommands.js";
import { t } from "../i18n/i18n.js";
import { DAILY_RESET_KEY_MODULE_FUSION } from "../models/ModuleBrickSaveData.js";
import { isSystemUnlocked, subsystemRequiredBestChapter } from "../data/systemUnlockData.js";
import { viewTabHandle } from "@yjsgames/phaser-ui/tab-registry";
import type { DomainBootstrapper } from "./BootTypes.js";

export const composeBoot: DomainBootstrapper = {
  key: "compose",
  install(boot) {
    const { ctx } = boot;
    const infoDeps = { modals: boot.modals, ctx, screen: () => boot.screen() };

    const merge = new MergeCommands({
      ctx,
      ports: boot.ports,
      // 합성 성공 직후 결과 모듈 정보 팝업 — 전투 그리드 탭과 동일 팝업 (요구사항).
      onMergeResult: (id) => {
        view.markMergeResult(id);
        refresh();
        openModuleInfoModal(infoDeps, id);
      },
      onActionCompleted: (target) => boot.guideQuest.notifyActionCompleted(target),
    });
    const fusion = new FusionCommands({
      ctx,
      ports: boot.ports,
      adFree: boot.state.adFree,
      showRewardedAd: (callbacks) => boot.rewardedAd().showRewarded(callbacks),
      showToast: (m) => boot.ui.showToast(m),
    });
    const fusionRequiredChapter = subsystemRequiredBestChapter("moduleFusion");
    const fusionUnlocked = (): boolean =>
      isSystemUnlocked("subsystem", "moduleFusion", ctx.chapter().bestChapter);
    const showFusionLocked = (): void =>
      boot.ui.showToast(t("codex.lockedToast", { chapter: fusionRequiredChapter }));

    /** 셀 탭 — 모듈 정보 팝업 + 하단 등록/해제 버튼 (merge/fusion 동형, 기준만 다름). */
    const openCellInfo = (kind: "merge" | "fusion", instanceId: string): void => {
      const st =
        kind === "merge" ? view.mergeButtonState(instanceId) : view.fusionButtonState(instanceId);
      const label =
        st.action === "unregister" ? t("moduleInfo.unregister") : t("moduleInfo.register");
      openModuleInfoModal(infoDeps, instanceId, {
        label,
        enabled: st.enabled,
        onAction: () => {
          if (kind === "merge") view.toggleMergeMaterial(instanceId);
          else view.toggleFusionMaterial(instanceId);
          boot.modals.closeModuleInfo();
        },
      });
    };

    const tc = boot.layout.tabContent;
    // bottomY = 패널 배경 하단(tabPanelBottom) — 일반 탭(statUpgrade/rebirth)과 동일 기준.
    // tabContent 하단(패널 inset)이 아니라 패널 하단을 써야 서브탭 바가 메인 하단 탭바에 바짝 붙는다(사용자 요청).
    const view = new ModuleComposeContainerTabView(
      boot.scene,
      tc.x,
      tc.y,
      tc.width,
      boot.layout.shell.tabPanelBottom,
      {
        merge: {
          onMerge: (ids) => merge.merge(ids),
          onCellInfo: (id) => openCellInfo("merge", id),
        },
        fusion: {
          onFuse: (ids) => fusion.fuse(ids),
          onCellInfo: (id) => openCellInfo("fusion", id),
        },
        fusionEnabled: boot.features.moduleFusion,
        isFusionUnlocked: fusionUnlocked,
        onFusionLocked: showFusionLocked,
        onSubChange: () => refresh(),
      },
    );

    /** 두 서브탭 모두 최신화 (숨은 서브탭 update는 무해). 융합 비용은 adFree 반영. */
    function refresh(): void {
      const levelOf = (id: string): number => ctx.playerModuleLevels().level(id);
      view.updateMerge(
        buildModuleMergeData(ctx.inventory(), ctx.grid(), ctx.moduleRegistry(), levelOf),
      );
      view.updateFusion(
        buildModuleFusionData(
          ctx.inventory(),
          ctx.grid(),
          ctx.moduleRegistry(),
          ctx.fusion().cost(boot.state.adFree()),
          levelOf,
        ),
      );
    }

    boot.tabs.register("moduleMerge", viewTabHandle(view, refresh));
    boot.api.register({
      performModuleMerge: (ids) => merge.merge(ids),
      performModuleFusion: (ids) => {
        if (!fusionUnlocked()) {
          showFusionLocked();
          return;
        }
        fusion.fuse(ids);
      },
    });
    // 자정 리셋 — 융합 사용 횟수 0 복귀 (다시 첫 회 광고).
    boot.dailyResets.register(DAILY_RESET_KEY_MODULE_FUSION, () => ctx.fusion().applyReset());
  },
};
